import React, { useEffect, useState, useRef } from "react";
import { FaUserCircle, FaCamera, FaTrash } from "react-icons/fa";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../firebase/firebase";
import { IconWrapper } from "../components/IconWrapper";
import AddNewAdminModal from "../components/AddNewAdminModal";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";

interface User {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  dept?: string;
  permissions?: string;
  profileImage?: string;
  isOrgAdmin?: boolean;
}

interface ProfileMenuProps {
  currentUser: User | null;
  bg: string;
  textColor: string;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  currentUser,
  bg,
  textColor,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState<User | null>(currentUser);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>("");
  const [toastMsg, setToastMsg] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [lastSyncedData, setLastSyncedData] = useState<User | null>(userData);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdminUser = currentUser?.isOrgAdmin || currentUser?.role?.toLowerCase() === "admin" || currentUser?.permissions?.toLowerCase() === "admin";
  const canEditDept = userData?.role?.toLowerCase() === "admin" || userData?.role?.toLowerCase() === "manager" || userData?.permissions?.toLowerCase() === "admin";

  const handleInputChange = (field: keyof User, value: string) => {
    if (userData) {
      setUserData({ ...userData, [field]: value });
    }
  };

  const handleSave = async () => {
    if (!userData) {
      setToastMsg({
        type: "error",
        message: "No user data available to update.",
      });
      return;
    }
    setIsSaving(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error("No authenticated user found.");
      const orgSnapshot = await getDocs(collection(db, "organization"));
      let organizationId = null;
      for (const orgDoc of orgSnapshot.docs) {
        const orgData = orgDoc.data();
        const member = orgData.members?.find((m: any) => m.id === userId);
        if (member) {
          organizationId = orgDoc.id;
          break;
        }
      }
      if (!organizationId)
        throw new Error("Organization not found for the current user.");
      const orgRef = doc(db, "organization", organizationId);
      const orgDocSnap = await getDoc(orgRef);
      const orgData = orgDocSnap.data();
      if (!orgData || !orgData.members)
        throw new Error("Invalid organization data.");
      const updatedMembers = orgData.members.map((member: any) =>
        member.id === userId ? { ...member, ...userData, profileImage } : member
      );
      await updateDoc(orgRef, { members: updatedMembers });
      // 1. Sync into users collection
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        dept: userData.dept || null,
        role: userData.role,
        profileImage: profileImage || "",
      });

      // 2. If they’re an admin, sync into admins collection as well
      if (userData.role === "admin") {
        const adminRef = doc(db, "admins", userId);
        // use setDoc if the doc might not exist yet, or updateDoc otherwise
        await updateDoc(adminRef, {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email, // admins collection stores email too
          phone: userData.phone,
          dept: userData.dept || null,
          role: userData.role,
        });
      }
      setToastMsg({
        type: "success",
        message: "Profile updated successfully.",
      });
      setIsEditing(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.";
      setToastMsg({ type: "error", message: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  /*  useEffect(() => {
    if (currentUser) setUserData(currentUser);
  }, [currentUser]);  */

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return; // no user → no listener

    // subscribe to live updates on users/{uid}
    const unsubscribe = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as User;
          setUserData(data);
          setLastSyncedData(data);
        }
      },
      (err) => console.error("[Profile] onSnapshot error:", err)
    );

    return unsubscribe; // unsubscribe on unmount
  }, []);

  // Fetch organization ID for the current user
  useEffect(() => {
    const fetchOrganizationId = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      try {
        console.log("Fetching organization ID for user:", uid);
        const orgSnapshot = await getDocs(collection(db, "organization"));
        console.log("Found organizations:", orgSnapshot.docs.length);

        for (const orgDoc of orgSnapshot.docs) {
          const orgData = orgDoc.data();
          console.log("Checking organization:", orgDoc.id, "with members:", orgData.members?.length || 0);

          const member = orgData.members?.find((m: any) => m.id === uid);
          if (member) {
            setOrganizationId(orgDoc.id);

            if (member.profileImage) {
              setProfileImage(member.profileImage);
            }
            break;
          }
        }
      } catch (error) {
        console.error("Error fetching organization ID:", error);
      }
    };

    fetchOrganizationId();
  }, []);

  // Fetch departments when organizationId is available
  useEffect(() => {
    if (!organizationId) return;
    const fetchDepartments = async () => {
      try {
        const q = collection(db, `organization/${organizationId}/departments`);
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, name: d.data().name }));
        setDepartments(list);
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
    };
    fetchDepartments();
  }, [organizationId]);

  const handleCancel = () => {
    setUserData(lastSyncedData);
    setIsEditing(false);
  };

  const isValidImageUrl = (url?: string): boolean => {
    if (!url) return false;
    // Local device URIs (from mobile ImagePicker) aren't accessible from web
    if (url.startsWith("file://") || url.startsWith("content://")) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userData) return;

    const userId = auth.currentUser?.uid;
    if (!userId) return;

    try {
      setIsUploadingImage(true);

      const storageRef = ref(storage, `profileImages/${userId}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const orgSnapshot = await getDocs(collection(db, "organization"));
      let orgId = organizationId;
      if (!orgId) {
        for (const orgDoc of orgSnapshot.docs) {
          const orgData = orgDoc.data();
          const member = orgData.members?.find((m: any) => m.id === userId);
          if (member) {
            orgId = orgDoc.id;
            break;
          }
        }
      }

      if (orgId) {
        const orgRef = doc(db, "organization", orgId);
        const orgDocSnap = await getDoc(orgRef);
        const orgData = orgDocSnap.data();
        if (orgData?.members) {
          const updatedMembers = orgData.members.map((member: any) =>
            member.id === userId ? { ...member, profileImage: downloadURL } : member
          );
          await updateDoc(orgRef, { members: updatedMembers });
        }
      }

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { profileImage: downloadURL }).catch(() => {});

      setProfileImage(downloadURL);
      setToastMsg({ type: "success", message: "Profile picture updated." });
    } catch (error) {
      console.error("Image upload failed:", error);
      setToastMsg({ type: "error", message: "Failed to upload image. Please try again." });
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !profileImage) return;

    try {
      setIsUploadingImage(true);

      const storageRef = ref(storage, `profileImages/${userId}`);
      await deleteObject(storageRef).catch(() => {});

      const orgSnapshot = await getDocs(collection(db, "organization"));
      let orgId = organizationId;
      if (!orgId) {
        for (const orgDoc of orgSnapshot.docs) {
          const orgData = orgDoc.data();
          const member = orgData.members?.find((m: any) => m.id === userId);
          if (member) {
            orgId = orgDoc.id;
            break;
          }
        }
      }

      if (orgId) {
        const orgRef = doc(db, "organization", orgId);
        const orgDocSnap = await getDoc(orgRef);
        const orgData = orgDocSnap.data();
        if (orgData?.members) {
          const updatedMembers = orgData.members.map((member: any) =>
            member.id === userId ? { ...member, profileImage: "" } : member
          );
          await updateDoc(orgRef, { members: updatedMembers });
        }
      }

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { profileImage: "" }).catch(() => {});

      setProfileImage("");
      setToastMsg({ type: "success", message: "Profile picture removed." });
    } catch (error) {
      console.error("Image remove failed:", error);
      setToastMsg({ type: "error", message: "Failed to remove image." });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Toast auto-dismiss
  useEffect(() => {
    if (toastMsg) {
      const timer = setTimeout(() => setToastMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMsg]);

  // Debug logging for modal props
  useEffect(() => {
    console.log("Modal props:", {
      isOpen: isAdminModalOpen,
      currentAdminId: auth.currentUser?.uid,
      organizationId: organizationId
    });
  }, [isAdminModalOpen, organizationId]);

  return (
    <div className={`w-full ${theme.background} flex items-start justify-center py-8 px-2`}>
      <div className={`w-full max-w-4xl rounded-2xl p-0 sm:p-6 md:p-8 lg:p-10 shadow-sm border ${theme.border} ${theme.card}`}>
        <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10 mb-8">
          <div className="flex flex-col items-center md:items-start">
            <div className="relative mb-2 group">
              {isValidImageUrl(profileImage) ? (
                <img
                  src={profileImage}
                  alt={`${userData?.firstName} ${userData?.lastName}`}
                  className={`w-24 h-24 rounded-full object-cover ring-4 ${isDark ? "ring-[#2E2F33]" : "ring-[#E5E7EB]"}`}
                />
              ) : (
                <div className={`w-24 h-24 rounded-full flex items-center justify-center ring-4 ${isDark ? "ring-[#2E2F33] bg-gradient-to-br from-[#FBD282] to-[#B59143]" : "ring-[#E5E7EB] bg-gradient-to-br from-[#FBD282] to-[#B59143]"}`}>
                  <IconWrapper
                    icon={FaUserCircle}
                    size={60}
                    className="text-white"
                  />
                </div>
              )}
              <span className={`absolute bottom-2 right-2 block w-4 h-4 rounded-full bg-green-400 border-2 ${isDark ? "border-[#1A1C1F]" : "border-white"}`}></span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className={`absolute inset-0 w-24 h-24 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${isUploadingImage ? "pointer-events-none" : ""}`}
              >
                {isUploadingImage ? (
                  <span className="text-white text-xs font-medium">Uploading...</span>
                ) : (
                  <IconWrapper icon={FaCamera} size={20} className="text-white" />
                )}
              </button>
              {isValidImageUrl(profileImage) && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={isUploadingImage}
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
                >
                  <IconWrapper icon={FaTrash} size={10} className="text-white" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1">
            <h1 className={`text-3xl font-extrabold mb-1 font-sans tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
              Personal Information
            </h1>
            <p className={`text-base ${theme.textMuted}`}>
              Update your personal details
            </p>
            {toastMsg && (
              <div
                className={`mt-4 px-4 py-2 rounded text-white text-sm font-medium ${toastMsg.type === "success" ? "bg-green-500" : "bg-red-500"
                  }`}
              >
                {toastMsg.message}
              </div>
            )}
          </div>
        </div>
        <hr className={`my-6 ${theme.border}`} />
        <div className="mb-6">
          <h2 className={`text-lg font-semibold mb-2 tracking-wide ${isDark ? "text-white" : "text-gray-900"}`}>
            Profile Details
          </h2>
        </div>
        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                First Name
              </label>
              <input
                type="text"
                value={userData?.firstName || ""}
                readOnly={!isEditing}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${isEditing
                  ? `${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white text-gray-900 border-[#E5E7EB]"}`
                  : `${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-900 border-gray-100"}`
                  } focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]`}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
            </div>
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                Last Name
              </label>
              <input
                type="text"
                value={userData?.lastName || ""}
                readOnly={!isEditing}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${isEditing
                  ? `${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white text-gray-900 border-[#E5E7EB]"}`
                  : `${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-900 border-gray-100"}`
                  } focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]`}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                Email
              </label>
              <div className="relative group">
                <input
                  type="email"
                  value={userData?.email || ""}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg cursor-not-allowed ${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-500 border-gray-100"
                    }`}
                />
                {/* Lock icon — appears on hover */}
                <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${isDark ? 'text-[#6B7280]' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12 1a5 5 0 00-5 5v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 7V6a3 3 0 10-6 0v2h6zm-3 4a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                Phone
              </label>
              <input
                type="text"
                value={userData?.phone || ""}
                readOnly={!isEditing}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${isEditing
                  ? `${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white text-gray-900 border-[#E5E7EB]"}`
                  : `${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-900 border-gray-100"}`
                  } focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]`}
                onChange={(e) => handleInputChange("phone", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                Department
              </label>
              {isEditing && canEditDept ? (
                <div className="relative">
                  <select
                    value={userData?.dept || ""}
                    onChange={(e) => handleInputChange("dept", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition appearance-none ${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white text-gray-900 border-[#E5E7EB]"
                      } focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="relative group">
                  <input
                    type="text"
                    value={userData?.dept || "None"}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg cursor-not-allowed ${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-500 border-gray-100"
                      }`}
                  />
                  {/* Lock icon — appears on hover when editing but no permission, or not editing */}
                  <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${isDark ? 'text-[#6B7280]' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 1a5 5 0 00-5 5v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 7V6a3 3 0 10-6 0v2h6zm-3 4a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className={`block text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-gray-700"}`}>
                Role
              </label>
              {/* Role is always read-only for non-admin/non-manager users; wraps with lock icon on hover */}
              {isEditing && (currentUser?.role?.toLowerCase() === "admin" || currentUser?.role?.toLowerCase() === "manager" || currentUser?.permissions?.toLowerCase() === "admin") ? (
                <input
                  type="text"
                  value={userData?.role || ""}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white text-gray-900 border-[#E5E7EB]"
                    } focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]`}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                />
              ) : (
                <div className="relative group">
                  <input
                    type="text"
                    value={userData?.role || ""}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg cursor-not-allowed ${isDark ? "bg-[#14161B] text-[#6B7280] border-[#2E2F33]" : "bg-gray-50 text-gray-500 border-gray-100"
                      }`}
                  />
                  {/* Lock icon — appears on hover */}
                  <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${isDark ? 'text-[#6B7280]' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 1a5 5 0 00-5 5v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 7V6a3 3 0 10-6 0v2h6zm-3 4a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-end items-center gap-4 pt-4">

            {/* ── Action buttons ── */}
            <div className="flex flex-col md:flex-row gap-4">
              {isAdminUser && !isEditing && (
                <button
                  type="button"
                  className={`px-8 py-2 rounded-full font-semibold transition w-full md:w-auto ${theme.goldOutlineButton}`}
                  onClick={() => setIsAdminModalOpen(true)}
                >
                  Add New Admin
                </button>
              )}
              {isEditing ? (
                <>
                  <button
                    type="button"
                    className={`px-8 py-2 rounded-full font-semibold transition disabled:opacity-60 w-full md:w-auto shadow-sm ${theme.primaryButton}`}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    className={`px-8 py-2 rounded-full font-semibold transition w-full md:w-auto ${theme.secondaryButton}`}
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={`px-8 py-2 rounded-full font-semibold transition w-full md:w-auto shadow-sm ${theme.primaryButton}`}
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </form>
        {!userData && (
          <div className="text-center py-8">
            <span className={`text-lg font-medium ${theme.textMuted}`}>
              No user information available.
            </span>
          </div>
        )}
      </div>

      {/* Add New Admin Modal */}
      <AddNewAdminModal
        isOpen={isAdminModalOpen}
        onClose={() => setIsAdminModalOpen(false)}
        currentAdminId={auth.currentUser?.uid || ""}
        organizationId={organizationId}
      />
    </div>
  );
};

export default ProfileMenu;

// import React, { useEffect, useState } from "react";
// import {
//   IconButton,
//   FormControl,
//   FormLabel,
//   Input,
//   Select,
//   Button,
//   Text,
//   VStack,
//   HStack,
//   useToast,
//   Box,
//   Divider,
//   Heading,
//   Icon,
// } from "@chakra-ui/react";
// import { FaUserCircle } from "react-icons/fa";
// import {
//   doc,
//   getDoc,
//   updateDoc,
//   collection,
//   getDocs,
// } from "firebase/firestore";
// import { db, auth } from "../firebase/firebase"; // Ensure you import your Firebase configuration

// interface User {
//   firstName: string;
//   lastName: string;
//   email: string;
//   phone: string;
//   role: string;
//   dept?: string; // Optional department field
// }

// interface ProfileMenuProps {
//   currentUser: User | null;
//   bg: string;
//   textColor: string;
// }

// const ProfileMenu: React.FC<ProfileMenuProps> = ({
//   currentUser,
//   bg,
//   textColor,
// }) => {
//   const [isEditing, setIsEditing] = useState(false);
//   const [userData, setUserData] = useState<User | null>(currentUser);
//   const toast = useToast();

//   const handleInputChange = (field: keyof User, value: string) => {
//     if (userData) {
//       setUserData({ ...userData, [field]: value });
//     }
//   };

//   const handleSave = async () => {
//     if (!userData) {
//       toast({
//         title: "Error",
//         description: "No user data available to update.",
//         status: "error",
//         duration: 3000,
//         isClosable: true,
//       });
//       return;
//     }

//     try {
//       const userId = auth.currentUser?.uid;
//       if (!userId) {
//         throw new Error("No authenticated user found.");
//       }

//       // Fetch the organization where the user is a member
//       const orgSnapshot = await getDocs(collection(db, "organization"));
//       let organizationId = null;

//       for (const orgDoc of orgSnapshot.docs) {
//         const orgData = orgDoc.data();
//         const member = orgData.members?.find((m: any) => m.id === userId);

//         if (member) {
//           organizationId = orgDoc.id;
//           break;
//         }
//       }

//       if (!organizationId) {
//         throw new Error("Organization not found for the current user.");
//       }

//       const orgRef = doc(db, "organization", organizationId);
//       const orgDoc = await getDoc(orgRef);
//       const orgData = orgDoc.data();

//       if (!orgData || !orgData.members) {
//         throw new Error("Invalid organization data.");
//       }

//       const updatedMembers = orgData.members.map((member: any) =>
//         member.id === userId ? { ...member, ...userData } : member
//       );

//       // Update the organization document with the modified members array
//       await updateDoc(orgRef, { members: updatedMembers });

//       toast({
//         title: "Success",
//         description: "Profile updated successfully.",
//         status: "success",
//         duration: 3000,
//         isClosable: true,
//       });

//       setIsEditing(false);
//     } catch (error: unknown) {
//       console.error("Error updating profile:", error);

//       // Use a type guard to ensure it's an Error instance
//       const errorMessage =
//         error instanceof Error
//           ? error.message
//           : "An unexpected error occurred.";

//       toast({
//         title: "Error",
//         description: errorMessage,
//         status: "error",
//         duration: 3000,
//         isClosable: true,
//       });
//     }
//   };
//   useEffect(() => {
//     if (currentUser) {
//       setUserData(currentUser);
//     }
//   }, [currentUser]);

//   const handleCancel = () => {
//     setUserData(currentUser); // Revert changes
//     setIsEditing(false);
//   };

//   console.log("userData", userData);
//   return (
//     <Box p={5} maxWidth="800px" mx="auto">
//       <Heading as="h1" size="xl" mb={2}>
//         Personal Information
//       </Heading>
//       <Text fontSize="lg" color="gray.600" mb={6}>
//         Update your personal details
//       </Text>

//       <HStack spacing={6} align="start" mb={8}>
//         <Box
//           bg="blue.100"
//           borderRadius="md"
//           p={4}
//           display="flex"
//           alignItems="center"
//           justifyContent="center"
//           boxSize="80px"
//         >
//           <Icon as={FaUserCircle} boxSize="50px" color="blue.600" />
//         </Box>
//         <VStack align="start" spacing={1}>
//           <Text fontSize="lg" fontWeight="bold">
//             Your Profile
//           </Text>
//           <Text fontSize="sm" color="gray.500">
//             Personal Information
//           </Text>
//         </VStack>
//       </HStack>

//       <VStack spacing={6} align="stretch">
//         {userData ? (
//           <>
//             <HStack spacing={4} alignItems="flex-start">
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">First Name</FormLabel>
//                 <Input
//                   value={userData.firstName}
//                   isReadOnly={!isEditing}
//                   borderColor="gray.300"
//                   onChange={(e) => handleInputChange("firstName", e.target.value)}
//                   color={textColor}
//                 />
//               </FormControl>
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">Last Name</FormLabel>
//                 <Input
//                   value={userData.lastName}
//                   isReadOnly={!isEditing}
//                   borderColor="gray.300"
//                   onChange={(e) => handleInputChange("lastName", e.target.value)}
//                   color={textColor}
//                 />
//               </FormControl>
//             </HStack>

//             <HStack spacing={4} alignItems="flex-start">
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">Email</FormLabel>
//                 <Input
//                   value={userData.email}
//                   isReadOnly={true}
//                   borderColor="gray.300"
//                   color={textColor}
//                 />
//               </FormControl>
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">Phone</FormLabel>
//                 <Input
//                   value={userData.phone}
//                   isReadOnly={!isEditing}
//                   borderColor="gray.300"
//                   onChange={(e) => handleInputChange("phone", e.target.value)}
//                   color={textColor}
//                 />
//               </FormControl>
//             </HStack>

//             <HStack spacing={4} alignItems="flex-start">
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">Department</FormLabel>
//                 <Input
//                   value={userData.dept || "None"}
//                   isReadOnly={!isEditing}
//                   borderColor="gray.300"
//                   onChange={(e) => handleInputChange("dept", e.target.value)}
//                   color={textColor}
//                 />
//               </FormControl>
//               <FormControl flex="1">
//                 <FormLabel fontWeight="bold" color="black">Role</FormLabel>
//                 {currentUser?.role === "admin" && isEditing ? (
//                   <Input
//                     value={userData.role}
//                     isReadOnly={!isEditing}
//                     borderColor="gray.300"
//                     onChange={(e) => handleInputChange("role", e.target.value)}
//                     color={textColor}
//                   ></Input>
//                 ) : (
//                   <Input
//                     value={userData.role}
//                     isReadOnly={true}
//                     borderColor="gray.300"
//                     color={textColor}
//                   />
//                 )}
//               </FormControl>
//             </HStack>

//             <HStack justifyContent="flex-end" mt={4}>
//               {isEditing ? (
//                 <>
//                   <Button colorScheme="purple" onClick={handleSave} color="black">
//                     Save Changes
//                   </Button>
//                   <Button colorScheme="purple" onClick={handleCancel} color="black">
//                     Cancel
//                   </Button>
//                 </>
//               ) : (
//                 <Button colorScheme="purple" onClick={() => setIsEditing(true)} color="black">
//                   Edit Profile
//                 </Button>
//               )}
//             </HStack>
//           </>
//         ) : (
//           <Box textAlign="center" py={4}>
//             <Text fontSize="lg" fontWeight="bold">
//               No user information available.
//             </Text>
//           </Box>
//         )}
//       </VStack>
//     </Box>
//   );
// };

// export default ProfileMenu;
